/**
 * useTargetJourney — Target Journey state, blending real aggregator funnel
 * data (impressions → menu → cart → order from useAggregatorFunnel) with
 * sales-target + sales-MTD + inputs (Meta Ads, organic IG, etc.).
 *
 * Returns a combined `data` (null until both queries resolve) + `isLoading`.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';
import { useAggregatorFunnel } from '@/hooks/useAggregatorFunnel';
import { useAggregatorMetric } from '@/hooks/useAggregatorMetric';
import { formatINRCompact, formatNumberCompact } from '@/lib/format';
import type { JourneyEdge, JourneyState, NodeId, Status } from './types';

interface DailyRow {
  outlet_id: string;
  date: string;
  net_sales: number | null;
  orders: number | null;
  discount_total: number | null;
  addon_revenue: number | null;
}
interface MetaAdRow {
  date: string;
  spend: number | null;
  conversion_value: number | null;
  reach: number | null;
}
interface MetaBrandRow {
  date: string;
  reach: number | null;
  source: string | null;
}
interface AggRow {
  date: string;
  ads_spend: number | null;
  gmv: number | null;
}

function todayIST(): Date {
  return new Date(Date.now() + 5.5 * 3600 * 1000);
}
function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function monthStart(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}
function monthEnd(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
}

interface BaseData {
  resolvedTarget: number;
  mtdNet: number;
  mtdOrders: number;
  mtdDiscount: number;
  mtdAddon: number;
  aov: number;
  baseAov: number;
  addonPerOrder: number;
  addonLiftPct: number;
  discountPct: number;
  projected: number;
  monthElapsedPct: number;
  // inputs (sparse)
  metaSpend: number;
  metaRoas: number;
  metaReach: number;
  igReach: number;
  aggSpend: number;
  aggGmv: number;
  influenceCount: number;
  avgRating: number;
  minAvailability: number;
  availOutletDetail: string;
}

export type JourneyScope = 'this_month' | 'last_month';

function useBase(outletId: string | undefined, scope: JourneyScope) {
  return useQuery({
    queryKey: ['target-journey-base', outletId ?? 'all', scope],
    queryFn: async (): Promise<BaseData> => {
      const now = todayIST();
      // Reference date for the scope: today for this_month, end-of-last-month
      // for last_month. All ranges derive from this anchor.
      const anchor =
        scope === 'last_month'
          ? new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 15))
          : now;
      const mStart = monthStart(anchor);
      const mEnd = monthEnd(anchor);
      const since = isoDate(mStart);
      const rangeEnd = scope === 'last_month' ? isoDate(mEnd) : isoDate(now);

      const daysInMonth = mEnd.getUTCDate();
      // For last_month the period is closed → elapsed = full month.
      // For this_month we count up to and including today.
      const daysCompleted =
        scope === 'last_month'
          ? daysInMonth
          : Math.max(1, now.getUTCDate());
      const monthElapsedPct = daysCompleted / daysInMonth;

      const targetQ = await marketing
        .from('sales_target')
        .select('target_inr')
        .eq('scope', 'month')
        .eq('outlet_id', outletId ?? '')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      const resolvedTarget =
        (targetQ.data as { target_inr: number } | null)?.target_inr ?? 15000000;

      let salesQ = marketing
        .from('outlet_sales_v')
        .select('outlet_id, date, net_sales, orders, discount_total, addon_revenue')
        .gte('date', since)
        .lte('date', rangeEnd);
      if (outletId) salesQ = salesQ.eq('outlet_id', outletId);
      const sales = ((await salesQ).data ?? []) as DailyRow[];

      const mtdNet = sales.reduce((s, r) => s + (r.net_sales ?? 0), 0);
      const mtdOrders = sales.reduce((s, r) => s + (r.orders ?? 0), 0);
      const mtdDiscount = sales.reduce((s, r) => s + (r.discount_total ?? 0), 0);
      const mtdAddon = sales.reduce((s, r) => s + (r.addon_revenue ?? 0), 0);
      const aov = mtdOrders > 0 ? mtdNet / mtdOrders : 0;
      // Base AOV = ticket without add-ons. Lift = how much add-ons bump AOV.
      const baseAov = mtdOrders > 0 ? (mtdNet - mtdAddon) / mtdOrders : 0;
      const addonPerOrder = mtdOrders > 0 ? mtdAddon / mtdOrders : 0;
      const addonLiftPct = baseAov > 0 ? (addonPerOrder / baseAov) * 100 : 0;
      const discountPct =
        mtdNet + mtdDiscount > 0 ? (mtdDiscount / (mtdNet + mtdDiscount)) * 100 : 0;
      // For last_month the period is closed → "projected" = actual net.
      const projected =
        scope === 'last_month' ? mtdNet : mtdNet / Math.max(monthElapsedPct, 0.01);

      const metaQ = await marketing
        .from('meta_ad_daily')
        .select('date, spend, conversion_value, reach')
        .gte('date', since)
        .lte('date', rangeEnd);
      const metaRows = (metaQ.data ?? []) as MetaAdRow[];
      const metaSpend = metaRows.reduce((s, r) => s + (r.spend ?? 0), 0);
      const metaConvValue = metaRows.reduce((s, r) => s + (r.conversion_value ?? 0), 0);
      const metaReach = metaRows.reduce((s, r) => s + (r.reach ?? 0), 0);
      const metaRoas = metaSpend > 0 ? metaConvValue / metaSpend : 0;

      const igQ = await marketing
        .from('meta_brand_daily')
        .select('date, reach, source')
        .gte('date', since)
        .lte('date', rangeEnd);
      const igRows = ((igQ.data ?? []) as MetaBrandRow[]).filter((r) => r.source === 'instagram');
      const igReach = igRows.reduce((s, r) => s + (r.reach ?? 0), 0);

      const aggQ = await marketing
        .from('aggregator_daily')
        .select('date, ads_spend, gmv')
        .gte('date', since)
        .lte('date', rangeEnd);
      const aggRows = (aggQ.data ?? []) as AggRow[];
      const aggSpend = aggRows.reduce((s, r) => s + (r.ads_spend ?? 0), 0);
      const aggGmv = aggRows.reduce((s, r) => s + (r.gmv ?? 0), 0);

      let influenceCount = 0;
      try {
        const infQ = await marketing
          .from('influencers')
          .select('ig_handle', { count: 'exact', head: true });
        influenceCount = infQ.count ?? 0;
      } catch {}

      let avgRating = 0;
      try {
        const rs = await marketing
          .from('rating_snapshots')
          .select('rating')
          .order('captured_at', { ascending: false })
          .limit(50);
        const ratings = ((rs.data ?? []) as Array<{ rating: number | null }>)
          .map((r) => r.rating ?? 0)
          .filter(Boolean);
        avgRating =
          ratings.length > 0 ? ratings.reduce((s, x) => s + x, 0) / ratings.length : 0;
      } catch {}

      let minAvailability = 100;
      let availOutletDetail = '';
      try {
        const av = await marketing
          .from('availability_per_outlet_v')
          .select('outlet_id, availability_pct')
          .order('availability_pct', { ascending: true })
          .limit(3);
        const rows = (av.data ?? []) as Array<{
          outlet_id: string;
          availability_pct: number | null;
        }>;
        if (rows.length > 0) {
          minAvailability = rows[0]?.availability_pct ?? 100;
          availOutletDetail = rows
            .filter((r) => (r.availability_pct ?? 100) < 95)
            .map((r) => `${r.outlet_id} ${Math.round(r.availability_pct ?? 100)}%`)
            .join(', ');
        }
      } catch {}

      return {
        resolvedTarget,
        mtdNet,
        mtdOrders,
        mtdDiscount,
        mtdAddon,
        aov,
        baseAov,
        addonPerOrder,
        addonLiftPct,
        discountPct,
        projected,
        monthElapsedPct,
        metaSpend,
        metaRoas,
        metaReach,
        igReach,
        aggSpend,
        aggGmv,
        influenceCount,
        avgRating,
        minAvailability,
        availOutletDetail,
      };
    },
    staleTime: 60_000,
  });
}

export function useTargetJourney(outletId?: string, scope: JourneyScope = 'this_month') {
  const base = useBase(outletId, scope);
  // Both useAggregatorFunnel and useAggregatorMetric accept a PeriodKey;
  // map our scope to the right one (`month` = this month, `last_month` = previous).
  const periodKey = scope === 'last_month' ? 'last_month' : 'month';
  const funnel = useAggregatorFunnel({
    outletId: outletId ?? 'all',
    period: periodKey,
    providers: ['swiggy', 'zomato'],
  });
  // Aggregator-side restaurant-funded discount (Swiggy + Zomato exports).
  const aggDiscount = useAggregatorMetric({
    outletId: outletId ?? 'all',
    period: periodKey,
    concept: 'discount.restaurant_funded',
    providers: ['swiggy', 'zomato'],
  });
  // Aggregator ads — same source as /aggregators Ads tab (fooddb). The
  // marketing.aggregator_daily table I was reading before is mostly empty;
  // fooddb.flax_metrics_daily has the real Swiggy + Zomato spend.
  const aggAdsSpend = useAggregatorMetric({
    outletId: outletId ?? 'all',
    period: periodKey,
    concept: 'ads.spend',
    providers: ['swiggy', 'zomato'],
  });
  const aggAdsOrders = useAggregatorMetric({
    outletId: outletId ?? 'all',
    period: periodKey,
    concept: 'ads.orders',
    providers: ['swiggy', 'zomato'],
  });
  // Aggregator-side "Earned" — rating proxy via poor-rated share. Lower
  // poor-rated share = healthier earned trust.
  const totalRated = useAggregatorMetric({
    outletId: outletId ?? 'all',
    period: periodKey,
    concept: 'ratings.total_rated',
    providers: ['swiggy', 'zomato'],
  });
  const poorRated = useAggregatorMetric({
    outletId: outletId ?? 'all',
    period: periodKey,
    concept: 'ratings.poor_rated',
    providers: ['swiggy', 'zomato'],
  });
  // Channel split — Swiggy + Zomato net sales by provider. Online = sum;
  // dine-in = base MTD net - online (the residual, captures walk-in + pickup
  // direct sales that don't flow through aggregator exports).
  const swiggyNet = useAggregatorMetric({
    outletId: outletId ?? 'all',
    period: periodKey,
    concept: 'sales.net_sales',
    providers: ['swiggy'],
  });
  const zomatoNet = useAggregatorMetric({
    outletId: outletId ?? 'all',
    period: periodKey,
    concept: 'sales.net_sales',
    providers: ['zomato'],
  });

  const data = useMemo<JourneyState | null>(() => {
    if (!base.data || !funnel.data) return null;
    return buildState(
      base.data,
      funnel.data,
      aggDiscount.data?.metric.current ?? 0,
      {
        aggAdsSpend: aggAdsSpend.data?.metric.current ?? 0,
        aggAdsOrders: aggAdsOrders.data?.metric.current ?? 0,
        totalRated: totalRated.data?.metric.current ?? 0,
        poorRated: poorRated.data?.metric.current ?? 0,
        swiggyNet: swiggyNet.data?.metric.current ?? 0,
        zomatoNet: zomatoNet.data?.metric.current ?? 0,
      },
      scope,
    );
  }, [base.data, funnel.data, aggDiscount.data, aggAdsSpend.data, aggAdsOrders.data, totalRated.data, poorRated.data, swiggyNet.data, zomatoNet.data, scope]);

  return { data, isLoading: base.isLoading || funnel.isLoading };
}

// ── Month-over-month comparison ──────────────────────────────────────────────

export interface MoMRow {
  key: string;
  label: string;
  kind: 'inr' | 'num' | 'pct' | 'rating';
  thisMonth: number;
  lastMonth: number;
  /** Lower is better (discount %, ad spend) — flip the delta colouring. */
  invert?: boolean;
}

/**
 * This-month (MTD) vs last-month (full) across the journey's core levers.
 * Reuses useBase for both scopes so the numbers match the circuit view.
 */
export function useJourneyMoM(outletId?: string) {
  const cur = useBase(outletId, 'this_month');
  const prev = useBase(outletId, 'last_month');

  const rows = useMemo<MoMRow[]>(() => {
    const c = cur.data;
    const p = prev.data;
    if (!c || !p) return [];
    const all: MoMRow[] = [
      { key: 'net', label: 'Net sales', kind: 'inr', thisMonth: c.mtdNet, lastMonth: p.mtdNet },
      { key: 'target', label: 'Target', kind: 'inr', thisMonth: c.resolvedTarget, lastMonth: p.resolvedTarget },
      { key: 'orders', label: 'Orders', kind: 'num', thisMonth: c.mtdOrders, lastMonth: p.mtdOrders },
      { key: 'aov', label: 'Avg order value', kind: 'inr', thisMonth: c.aov, lastMonth: p.aov },
      { key: 'discount', label: 'Discount %', kind: 'pct', thisMonth: c.discountPct, lastMonth: p.discountPct, invert: true },
      { key: 'addon', label: 'Add-on / order', kind: 'inr', thisMonth: c.addonPerOrder, lastMonth: p.addonPerOrder },
      { key: 'meta', label: 'Meta ad spend', kind: 'inr', thisMonth: c.metaSpend, lastMonth: p.metaSpend, invert: true },
      { key: 'igreach', label: 'IG reach', kind: 'num', thisMonth: c.igReach, lastMonth: p.igReach },
      { key: 'rating', label: 'Avg rating', kind: 'rating', thisMonth: c.avgRating, lastMonth: p.avgRating },
      { key: 'availability', label: 'Min availability %', kind: 'pct', thisMonth: c.minAvailability, lastMonth: p.minAvailability },
      { key: 'influencers', label: 'Influencers', kind: 'num', thisMonth: c.influenceCount, lastMonth: p.influenceCount },
    ];
    // Drop rows with no data in either month.
    return all.filter((r) => r.thisMonth > 0 || r.lastMonth > 0);
  }, [cur.data, prev.data]);

  return { rows, isLoading: cur.isLoading || prev.isLoading };
}

interface ExtraAggregatorInputs {
  aggAdsSpend: number;
  aggAdsOrders: number;
  totalRated: number;
  poorRated: number;
  swiggyNet: number;
  zomatoNet: number;
}

// ───────── Status thresholds + helpers ─────────

const TH = {
  ctr: { good: 3, warn: 1.5 },
  cart: { good: 25, warn: 12 },
  order: { good: 60, warn: 40 },
  discount: { good: 15, warn: 20 }, // discount is "lower is better"
  availability: { good: 95, warn: 90 },
  rating: { good: 4.4, warn: 4.2 },
  roas: { good: 3, warn: 2 },
} as const;

function status(val: number, goodAt: number, warnAt: number): Status {
  if (!Number.isFinite(val) || val === 0) return 'na';
  if (val >= goodAt) return 'good';
  if (val >= warnAt) return 'warn';
  return 'bad';
}
function inverseStatus(val: number, goodBelow: number, warnBelow: number): Status {
  if (val === 0) return 'na';
  if (val < goodBelow) return 'good';
  if (val < warnBelow) return 'warn';
  return 'bad';
}

// ───────── Build the JourneyState ─────────

function buildState(
  b: BaseData,
  f: ReturnType<typeof useAggregatorFunnel>['data'] & object,
  aggDiscountInr: number,
  extra: ExtraAggregatorInputs,
  scope: JourneyScope,
): JourneyState {
  const stages = f.stages;
  const impressions = stages.find((s) => s.key === 'impressions')?.metric.current ?? 0;
  const menuOpens = stages.find((s) => s.key === 'menu_opens')?.metric.current ?? 0;
  const cartsBuilt = stages.find((s) => s.key === 'carts_built')?.metric.current ?? 0;
  const ordersPlaced = stages.find((s) => s.key === 'orders_placed')?.metric.current ?? 0;

  const ctrPct = impressions > 0 ? (menuOpens / impressions) * 100 : 0;
  const cartPct = menuOpens > 0 ? (cartsBuilt / menuOpens) * 100 : 0;
  const orderPct = cartsBuilt > 0 ? (ordersPlaced / cartsBuilt) * 100 : 0;

  // ── Target-derived "required" values ──────────────────────────────────
  // For each funnel stage, compute what it would have needed to be to hit
  // the period target, given downstream conversion + AOV stay the same.
  // Status compares actual vs required → if target missed by 22%, the node
  // contributing to that miss shows red, not just generic thresholds.
  const periodAov = b.aov;
  const ordersRequired = periodAov > 0 ? b.resolvedTarget / periodAov : 0;
  // Use observed conversions for the "what would it have needed" math —
  // these are facts of the period, not aspirations. If conversion was poor,
  // upstream volume needed to compensate; the math surfaces that.
  const cartsRequired =
    orderPct > 0 ? ordersRequired / (orderPct / 100) : ordersRequired;
  const menuOpensRequired =
    cartPct > 0 ? cartsRequired / (cartPct / 100) : cartsRequired;
  const impressionsRequired =
    ctrPct > 0 ? menuOpensRequired / (ctrPct / 100) : menuOpensRequired;

  function attainmentStatus(actual: number, required: number): Status {
    if (required <= 0 || actual === 0) return 'na';
    const ratio = actual / required;
    if (ratio >= 0.95) return 'good';
    if (ratio >= 0.85) return 'warn';
    return 'bad';
  }

  // ── Per-node status ────────────────────────────────────────────────────
  const targetPct = b.resolvedTarget > 0 ? b.projected / b.resolvedTarget : 0;
  const targetStatus: Status =
    targetPct >= 1 ? 'good' : targetPct >= 0.9 ? 'warn' : 'bad';

  // Funnel stages are now graded by "did we deliver the volume the target
  // needed?" — NOT by generic CTR / cart-rate thresholds. A 3% CTR is good
  // in isolation but bad if 4% was needed to hit target.
  const reachStatus: Status = attainmentStatus(impressions, impressionsRequired);
  const visitorsStatus: Status = attainmentStatus(menuOpens, menuOpensRequired);
  const cartsStatus: Status = attainmentStatus(cartsBuilt, cartsRequired);
  const ordersStatus: Status = attainmentStatus(ordersPlaced, ordersRequired);

  const availStatus: Status =
    b.minAvailability >= TH.availability.good
      ? 'good'
      : b.minAvailability >= TH.availability.warn
        ? 'warn'
        : 'bad';

  const aovStatus: Status = b.aov === 0 ? 'na' : 'good';
  // Add-on lift: ≥8% green, 4–8% amber, <4% red (upsell leak).
  const addonStatus: Status =
    b.mtdAddon === 0
      ? 'na'
      : b.addonLiftPct >= 8
        ? 'good'
        : b.addonLiftPct >= 4
          ? 'warn'
          : 'bad';
  // Combined discount = Petpooja-side item discount + aggregator restaurant-
  // funded discount (Swiggy/Zomato). Recompute % against net + total discount.
  const combinedDiscount = b.mtdDiscount + aggDiscountInr;
  const combinedDiscountPct =
    b.mtdNet + combinedDiscount > 0
      ? (combinedDiscount / (b.mtdNet + combinedDiscount)) * 100
      : 0;
  const discountStatus: Status = inverseStatus(combinedDiscountPct, TH.discount.good, TH.discount.warn);

  const metaStatus: Status = status(b.metaRoas, TH.roas.good, TH.roas.warn);
  const igStatus: Status = b.igReach === 0 ? 'na' : 'good';

  // Aggregator true-ROAS for the journey input: same math as the Ads tab —
  // (ads_orders × period AOV) / ads_spend, NOT averaged daily ratios.
  const aggTrueRoas =
    extra.aggAdsSpend > 0 && b.aov > 0
      ? (extra.aggAdsOrders * b.aov) / extra.aggAdsSpend
      : 0;
  const aggAdsStatus: Status =
    extra.aggAdsSpend === 0 ? 'na' : status(aggTrueRoas, TH.roas.good, TH.roas.warn);

  // Earned trust via aggregator ratings — good = poor-rated share ≤ 5%,
  // warn ≤ 10%, else bad. Falls back to N/A when no ratings exist.
  const poorPct =
    extra.totalRated > 0 ? (extra.poorRated / extra.totalRated) * 100 : 0;
  const earnedStatus: Status =
    extra.totalRated === 0 ? 'na' : poorPct <= 5 ? 'good' : poorPct <= 10 ? 'warn' : 'bad';

  // Influencers: any non-zero counts is good for v1. We can re-tighten when
  // the influencer enrichment pass actually scores enough handles.
  const influenceStatus: Status = b.influenceCount === 0 ? 'na' : 'good';

  const fmtINR = (n: number): string => formatINRCompact(n);
  const fmtNum = (n: number): string => formatNumberCompact(n);

  const nodes: Record<NodeId, JourneyState['nodes'][NodeId]> = {
    meta_ads: {
      id: 'meta_ads',
      label: 'Meta Ads',
      value: b.metaSpend > 0 ? fmtINR(b.metaSpend) : '—',
      sub: b.metaRoas > 0 ? `ROAS ${b.metaRoas.toFixed(1)}×` : 'no sync yet',
      status: metaStatus,
      href: '/meta',
      icon: 'megaphone',
    },
    organic: {
      id: 'organic',
      label: 'Organic IG',
      value: b.igReach > 0 ? `${fmtNum(b.igReach)}` : '—',
      sub: 'IG reach MTD',
      status: igStatus,
      href: '/meta',
      icon: 'instagram',
    },
    aggr_ads: {
      id: 'aggr_ads',
      label: 'Aggregator ads',
      value: extra.aggAdsSpend > 0 ? fmtINR(extra.aggAdsSpend) : '—',
      sub:
        extra.aggAdsSpend > 0
          ? `ROAS ${aggTrueRoas.toFixed(1)}× · ${fmtNum(extra.aggAdsOrders)} orders`
          : 'no spend',
      status: aggAdsStatus,
      href: '/aggregators',
      icon: 'trending-up',
    },
    influence: {
      id: 'influence',
      label: 'Influencers',
      value: `${b.influenceCount}`,
      sub: 'enriched in pipeline',
      status: influenceStatus,
      href: '/influencers',
      icon: 'sparkles',
    },
    earned: {
      id: 'earned',
      label: 'Earned',
      value:
        extra.totalRated > 0
          ? `${(100 - poorPct).toFixed(0)}%`
          : '—',
      sub:
        extra.totalRated > 0
          ? `${fmtNum(extra.totalRated)} ratings · ${poorPct.toFixed(1)}% poor`
          : 'no ratings yet',
      status: earnedStatus,
      href: '/aggregators',
      icon: 'star',
    },
    reach: {
      id: 'reach',
      label: 'Impressions',
      value: impressions > 0 ? fmtNum(impressions) : '—',
      sub:
        impressionsRequired > 0
          ? `${Math.round((impressions / impressionsRequired) * 100)}% of needed (${fmtNum(impressionsRequired)})`
          : 'Swiggy + Zomato',
      status: reachStatus,
      href: '/aggregators',
      icon: 'radio',
    },
    visitors: {
      id: 'visitors',
      label: 'Menu opens',
      value: menuOpens > 0 ? fmtNum(menuOpens) : '—',
      sub:
        menuOpensRequired > 0 && menuOpens > 0
          ? `${ctrPct.toFixed(1)}% CTR · ${Math.round((menuOpens / menuOpensRequired) * 100)}% of needed`
          : '— vs needed',
      status: visitorsStatus,
      href: '/aggregators',
      icon: 'users',
    },
    carts: {
      id: 'carts',
      label: 'Carts built',
      value: cartsBuilt > 0 ? fmtNum(cartsBuilt) : '—',
      sub:
        cartsRequired > 0 && cartsBuilt > 0
          ? `${cartPct.toFixed(1)}% of menu · ${Math.round((cartsBuilt / cartsRequired) * 100)}% of needed`
          : '— vs needed',
      status: cartsStatus,
      href: '/aggregators',
      icon: 'shopping-bag',
    },
    availability: {
      id: 'availability',
      label: 'Availability',
      value: `${Math.round(b.minAvailability)}%`,
      sub: b.availOutletDetail || 'all outlets ≥ 95%',
      status: availStatus,
      href: '/aggregators',
      icon: 'plug-zap',
    },
    orders: {
      id: 'orders',
      label: 'Orders',
      value: fmtNum(b.mtdOrders),
      sub:
        ordersRequired > 0
          ? `${orderPct.toFixed(1)}% conv · ${Math.round((b.mtdOrders / ordersRequired) * 100)}% of needed (${fmtNum(ordersRequired)})`
          : 'MTD all-channels',
      status: ordersStatus,
      href: '/sales',
      icon: 'shopping-bag',
    },
    aov: {
      id: 'aov',
      label: 'AOV',
      value: b.aov > 0 ? `₹${Math.round(b.aov)}` : '—',
      sub: 'avg ticket MTD',
      status: aovStatus,
      href: '/sales',
      icon: 'wallet',
    },
    discount: {
      id: 'discount',
      label: 'Discount drag',
      value: `${combinedDiscountPct.toFixed(1)}%`,
      sub: `Petpooja ${formatINRCompact(b.mtdDiscount)} + Aggr ${formatINRCompact(aggDiscountInr)} · T < 15%`,
      status: discountStatus,
      href: '/aggregators',
      icon: 'percent',
    },
    addons: {
      id: 'addons',
      label: 'Add-ons',
      value:
        b.addonPerOrder > 0 ? `+₹${Math.round(b.addonPerOrder)}/order` : '—',
      sub:
        b.mtdAddon > 0
          ? `+${b.addonLiftPct.toFixed(1)}% AOV lift · ${formatINRCompact(b.mtdAddon)} MTD · base AOV ₹${Math.round(b.baseAov)}`
          : 'no add-on revenue yet',
      status: addonStatus,
      href: '/sales',
      icon: 'sparkles',
    },
    channel_online: (() => {
      const online = extra.swiggyNet + extra.zomatoNet;
      const share = b.mtdNet > 0 ? (online / b.mtdNet) * 100 : 0;
      return {
        id: 'channel_online' as const,
        label: 'Online',
        value: online > 0 ? fmtINR(online) : '—',
        sub:
          online > 0
            ? `${share.toFixed(0)}% · Swiggy ${fmtINR(extra.swiggyNet)} · Zomato ${fmtINR(extra.zomatoNet)}`
            : 'Swiggy + Zomato',
        status: (online === 0 ? 'na' : 'good') as Status,
        href: '/aggregators',
        icon: 'shopping-bag',
      };
    })(),
    channel_dinein: (() => {
      const online = extra.swiggyNet + extra.zomatoNet;
      const dineIn = Math.max(b.mtdNet - online, 0);
      const share = b.mtdNet > 0 ? (dineIn / b.mtdNet) * 100 : 0;
      return {
        id: 'channel_dinein' as const,
        label: 'Dine-in + Pickup',
        value: dineIn > 0 ? fmtINR(dineIn) : '—',
        sub: dineIn > 0 ? `${share.toFixed(0)}% · walk-in + direct pickup` : '—',
        status: (dineIn === 0 ? 'na' : 'good') as Status,
        href: '/sales',
        icon: 'users',
      };
    })(),
    channel_b2b: {
      id: 'channel_b2b' as const,
      label: 'B2B + Catering',
      value: '—',
      sub: 'Zoho CRM not wired yet · click to set up',
      status: 'na' as Status,
      href: '/influencers', // placeholder until /crm route exists
      icon: 'sparkles',
    },
    target: {
      id: 'target',
      label: scope === 'last_month' ? 'Last month vs target' : 'Net Sales target',
      value: fmtINR(b.resolvedTarget),
      sub: (() => {
        const baseLine =
          scope === 'last_month'
            ? `Closed ${fmtINR(b.mtdNet)} · gap ${fmtINR(b.resolvedTarget - b.mtdNet)}`
            : `MTD ${fmtINR(b.mtdNet)} · Proj ${fmtINR(b.projected)}`;
        // If we missed (or projected to miss), name the biggest funnel-stage
        // shortfall right in the target node so the founder doesn't have to
        // scan the whole circuit.
        if (targetStatus === 'bad' || targetStatus === 'warn') {
          const candidates = [
            { name: 'impressions', actual: impressions, required: impressionsRequired },
            { name: 'menu opens', actual: menuOpens, required: menuOpensRequired },
            { name: 'carts', actual: cartsBuilt, required: cartsRequired },
            { name: 'orders', actual: b.mtdOrders, required: ordersRequired },
          ]
            .filter((c) => c.required > 0 && c.actual > 0)
            .map((c) => ({ ...c, ratio: c.actual / c.required }))
            .sort((a, b2) => a.ratio - b2.ratio);
          const worst = candidates[0];
          if (worst && worst.ratio < 0.95) {
            return `${baseLine} · ${worst.name} was ${Math.round(worst.ratio * 100)}% of needed`;
          }
        }
        return baseLine;
      })(),
      status: targetStatus,
      icon: 'zap',
    },
  };

  // ── Edges (with tooltips for every label) ──────────────────────────────
  const edges: JourneyEdge[] = [
    // Inputs → Impressions (Reach)
    { from: 'meta_ads', to: 'reach', contributionInr: b.metaSpend },
    { from: 'organic', to: 'reach', contributionInr: b.igReach * 0.5 },
    { from: 'aggr_ads', to: 'reach', contributionInr: b.aggSpend },
    { from: 'influence', to: 'reach', contributionInr: b.influenceCount * 5000 },
    { from: 'earned', to: 'reach', contributionInr: b.avgRating > 0 ? 50000 : 0 },

    // Funnel stages with real conversion %
    {
      from: 'reach',
      to: 'visitors',
      pctShare: ctrPct,
      tooltip: `CTR — share of impressions that opened the menu.\nNow ${ctrPct.toFixed(2)}% · target ≥ ${TH.ctr.good}%.`,
      blocked:
        impressions > 1000 && ctrPct < TH.ctr.warn
          ? {
              reason: `CTR ${ctrPct.toFixed(1)}% < ${TH.ctr.warn}% target`,
              costInr: ((TH.ctr.warn - ctrPct) / 100) * impressions * b.aov * (orderPct / 100) * (cartPct / 100),
            }
          : undefined,
    },
    {
      from: 'visitors',
      to: 'carts',
      pctShare: cartPct,
      tooltip: `Cart-add rate — share of menu opens that built a cart.\nNow ${cartPct.toFixed(1)}% · target ≥ ${TH.cart.good}%.`,
      blocked:
        menuOpens > 100 && cartPct < TH.cart.warn
          ? {
              reason: `cart rate ${cartPct.toFixed(1)}% < ${TH.cart.warn}% target`,
              costInr: ((TH.cart.warn - cartPct) / 100) * menuOpens * b.aov * (orderPct / 100),
            }
          : undefined,
    },
    {
      from: 'carts',
      to: 'orders',
      pctShare: orderPct,
      tooltip: `Cart-to-order conversion — share of carts that placed an order.\nNow ${orderPct.toFixed(1)}% · target ≥ ${TH.order.good}%.`,
      blocked:
        cartsBuilt > 50 && orderPct < TH.order.warn
          ? {
              reason: `cart→order ${orderPct.toFixed(1)}% < ${TH.order.warn}% target`,
              costInr: ((TH.order.warn - orderPct) / 100) * cartsBuilt * b.aov,
            }
          : undefined,
    },

    // Availability gate (modifier on visitors → carts)
    {
      from: 'availability',
      to: 'visitors',
      tooltip: `Online uptime — when an outlet is offline during paid hours, paid traffic is wasted.\nMin outlet now ${Math.round(b.minAvailability)}% · target ≥ ${TH.availability.good}%.`,
      blocked:
        b.minAvailability < TH.availability.good
          ? {
              reason: `availability ${Math.round(b.minAvailability)}% — ${b.availOutletDetail || 'min outlet'}`,
              costInr: ((TH.availability.good - b.minAvailability) / 100) * b.resolvedTarget,
            }
          : undefined,
    },

    // Orders split into the two channels that book revenue.
    // (Orders × AOV = Net Sales, broken out by where the orders came from.)
    (() => {
      const online = extra.swiggyNet + extra.zomatoNet;
      const onlineShare = b.mtdNet > 0 ? (online / b.mtdNet) * 100 : 0;
      return {
        from: 'orders' as const,
        to: 'channel_online' as const,
        pctShare: onlineShare,
        contributionInr: online,
        tooltip: `Online sales = Swiggy + Zomato delivered orders.\nSwiggy ${fmtINR(extra.swiggyNet)} · Zomato ${fmtINR(extra.zomatoNet)} · ${onlineShare.toFixed(0)}% of MTD net.`,
      };
    })(),
    // Dine-in is NOT downstream of the online funnel (carts/menu opens are an
    // aggregator-only path). Instead it's fed by reputation + brand reach +
    // influencer buzz — discovery signals that bring people in the door.
    {
      from: 'earned',
      to: 'channel_dinein',
      kind: 'discovery',
      tooltip: `Google + aggregator ratings drive walk-in trust.\nPoor-rated share ${poorPct.toFixed(1)}% of ${fmtNum(extra.totalRated)} ratings.`,
    },
    {
      from: 'organic',
      to: 'channel_dinein',
      kind: 'discovery',
      tooltip: `Organic IG reach (${fmtNum(b.igReach)} MTD) and location-tagged posts surface us to nearby browsers.`,
    },
    {
      from: 'influence',
      to: 'channel_dinein',
      kind: 'discovery',
      tooltip: `Influencer mentions (${b.influenceCount} active) seed dine-in foot traffic.`,
    },
    {
      from: 'channel_online',
      to: 'target',
      contributionInr: extra.swiggyNet + extra.zomatoNet,
      tooltip: `Online revenue flowing into the target.\n${fmtINR(extra.swiggyNet + extra.zomatoNet)} so far.`,
    },
    {
      from: 'channel_dinein',
      to: 'target',
      contributionInr: Math.max(b.mtdNet - (extra.swiggyNet + extra.zomatoNet), 0),
      tooltip: `Dine-in + pickup revenue flowing into the target.\n${fmtINR(Math.max(b.mtdNet - (extra.swiggyNet + extra.zomatoNet), 0))} so far.`,
    },
    {
      from: 'channel_b2b',
      to: 'target',
      contributionInr: 0,
      tooltip: `B2B revenue path — will populate once Zoho CRM is integrated and LinkedIn-attributed leads are tracked.`,
    },
    // AOV still feeds target — it's a multiplier on order volume, not its own
    // path. The edge gives the founder a place to hover for the AOV story.
    {
      from: 'aov',
      to: 'target',
      tooltip: `AOV (₹${Math.round(b.aov)}) multiplies order volume into revenue. Lifting AOV ₹50 with the same order count would add ${fmtINR(50 * b.mtdOrders)} to MTD.`,
    },

    // Discount erodes AOV; Add-ons lift AOV. Both feed into the AOV node so
    // the operator can see the two opposing forces in one place.
    {
      from: 'discount',
      to: 'aov',
      tooltip: `Discount drag eats into AOV. Now ${combinedDiscountPct.toFixed(1)}% of gross (Petpooja ${formatINRCompact(b.mtdDiscount)} + aggregator ${formatINRCompact(aggDiscountInr)}) · target keep < 15%.`,
    },
    {
      from: 'addons',
      to: 'aov',
      pctShare: b.addonLiftPct,
      contributionInr: b.mtdAddon,
      tooltip: `Add-ons lift AOV by +${b.addonLiftPct.toFixed(1)}%. Base AOV ₹${Math.round(b.baseAov)} → with add-ons ₹${Math.round(b.aov)}. ₹${Math.round(b.addonPerOrder)}/order · ${formatINRCompact(b.mtdAddon)} MTD.`,
    },
  ];

  return {
    target: {
      target: b.resolvedTarget,
      mtdNet: b.mtdNet,
      projected: b.projected,
      monthElapsedPct: b.monthElapsedPct,
    },
    nodes,
    edges,
  };
}
